import React, { useState, useEffect } from 'react';
import { Divider,Box, Button, Container, Grid, Paper, Typography, Dialog, IconButton, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PaymentForm from '../components/PaymentForm';
import LoadingScreen from '../components/LoadingScreen';
import instance from '../utils/axiosConfig';
import CloseIcon from '@mui/icons-material/Close';
import GradientBackground from '../components/GradientBackground';
import { auth } from '../utils/auth';

const PlanCard = ({ title, price, features = [], notIncluded = [], isPopular, onSelectPlan }) => {
  const navigate = useNavigate();

  const handleStartPlan = () => {
    if (!auth.isAuthenticated()) {
      navigate('/signup-distributor');
      return;
    }

    if (auth.getUserType() !== 'distributor') {
      navigate('/signup-distributor');
      return;
    }

    onSelectPlan(price);
  };

  return (
    <Paper 
      elevation={isPopular ? 8 : 2}
      sx={{
        p: 4,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        border: isPopular ? '2px solid primary.main' : 'none',
        '&:hover': {
          transform: 'scale(1.02)',
          transition: 'transform 0.6s ease-in-out',
        },
      
      }}
    >
      {isPopular && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            bgcolor: 'primary.main',
            color: 'white',
            px: 2,
            py: 0.5,
            borderRadius: 1,
          }}
        >
          Más Popular
        </Box>
      )}
      <Typography variant="h5" component="h2" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h4" component="h3" gutterBottom>
        {price == 0 ? 'Gratis' : `$${price} por mes`}
        {/* <Typography 
          component="span" 
          sx={{ 
            display: 'block',
            color: 'secondary.main',
            fontSize: '0.9rem',
            fontWeight: 'medium',
            mt: 0.5,
            animation: 'pulse 2s infinite'
          }}
        >
          Promoción por tiempo limitado
        </Typography> */}
      </Typography>
      <Box sx={{ flexGrow: 1 }}>
        {features?.map((feature, index) => (
          <Typography key={index} paragraph>
            • {feature}
          </Typography>
        ))}
        {notIncluded?.length > 0 && (
          <>
            <Divider sx={{ my: 4, border: '1px solid primary.main' }} />

            <Typography variant="subtitle1" sx={{ mt: 2, color: 'text.secondary' }}>
              No incluye:
            </Typography>
            {notIncluded.map((item, index) => (
              <Typography key={index} paragraph sx={{ color: 'text.secondary', mt: 1 }}>
                ✕ {item}
              </Typography>
            ))}
          </>
        )}
      </Box>
      <Button 
        variant={isPopular ? "contained" : "outlined"} 
        fullWidth 
        size="large"
        onClick={handleStartPlan}
      >
        {!auth.isAuthenticated() ? 'Registrarse' : 
         auth.getUserType() !== 'distributor' ? 'Registrarse como Distribuidor' : 
         'Empezar'}
      </Button>
    </Paper>
  );
}

const capitalizeWords = (str) => {
  return str.split(' ').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

const PricesAndPlans = () => {
  const [showPayment, setShowPayment] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPromo, setShowPromo] = useState(!auth.hasSeenPromoDialog());
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await instance.get('/api/plans');
        const data = await response.data;
        if (data.success) {
          // Format the plans for display
          const formattedPlans = data.plans.map(plan => ({
            ...plan,
            title: capitalizeWords(plan.title),
            price: plan.price,
            features: plan.features,
            notIncluded: plan.notIncluded
          }));
          setPlans(formattedPlans);
        }
      } catch (error) {
        console.error('Error fetching plans:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const handleSelectPlan = (price, plan) => {
    setSelectedPrice(price);
    setSelectedPlan(plan);
    setShowPayment(true);
  };

  if (loading) {
    return <LoadingScreen />;
  }
  return (
    <GradientBackground>
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 4 } }}>
        <Dialog
          open={showPromo}
          onClose={() => {
            setShowPromo(false);
            auth.markPromoDialogAsSeen();
          }}
          PaperProps={{
            sx: {
              borderRadius: 2,
              p: { xs: 2, md: 4 },
              maxWidth: '90vw',
              width: { xs: '90vw', sm: '450px' },
              background: 'linear-gradient(135deg,rgb(255, 255, 255) 50%,rgb(236, 236, 236) 100%)',
            }
          }}
        >
          <IconButton
            onClick={() => {
              setShowPromo(false);
              auth.markPromoDialogAsSeen();
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'primary.main'
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ textAlign: 'center', color: 'primary.main', mt: 2 }}>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                mb: 3,
                fontWeight: 'bold',
                fontSize: { xs: '1.5rem', md: '1.8rem' }
              }}
            >
              ¡Oferta Especial, solo por tiempo limitado!
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                fontSize: { xs: '1.1rem', md: '1.3rem' },
                lineHeight: 1.4
              }}
            >
              Registrate ahora para tener plan oro gratis por tres meses!
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => {
                setShowPromo(false);
                auth.markPromoDialogAsSeen();
                navigate('/signup-distributor');
              }}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                mb: 2,
                '&:hover': {
                  bgcolor: 'primary.dark',
                }
              }}
            >
              Registrarme Ahora
            </Button>
            <Button
              variant="text"
              onClick={() => {
                setShowPromo(false);
                auth.markPromoDialogAsSeen();
              }}
              sx={{
                color: 'text.secondary',
                fontSize: '0.9rem',
                '&:hover': {
                  bgcolor: 'transparent',
                  textDecoration: 'underline'
                }
              }}
            >
              No mostrar de nuevo
            </Button>
          </Box>
        </Dialog>
        <Typography
          variant="h3"
          component="h1"
          align="center"
          gutterBottom
          sx={{ 
            mb: 1,
            color: 'text.light',
            fontSize: { xs: '2rem', md: '3rem' },
          }}
        >
          Precios y Planes
        </Typography>
        <Typography 
        variant= 'h6'
          component= 'h6' 
          sx={{
          textAlign: 'center',
          color: 'text.light',
          fontSize: { xs: '0.9rem', md: '1.05rem' },
        }}>
          Solo para distribuidores/proveedores*
        </Typography>
        <Typography 
          variant="h6" 
          component="h2" 
          sx={{
            textAlign: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            pt: 0,
            mt: 0,
            color: 'text.light',
            fontSize: { xs: '0.9rem', md: '1.25rem' },
            lineHeight: { xs: 1.5, md: 1.75 },
            px: { xs: 2, md: 0 },
            pb: 2
          }} 
          gutterBottom
        >
          <span>Para realizar consultas,</span>
          <Link 
            component="button"
            onClick={() => navigate('/contact')}
            sx={{ 
              cursor: 'pointer',
              textTransform: 'none',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit',
              color: 'secondary.main',
              display: 'inline',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
             &nbsp;contactanos&nbsp;
          </Link>
          <span>a través de alguno de los medios disponibles</span>
          <Typography variant="h6" 
          component="h2"
          sx={{
            color: 'text.light',
            fontSize: { xs: '0.9rem', md: '1.25rem' },
          }}
          >
          Al comprar meses por adelantado, se te otorgarán descuentos de hasta un 22%.
        </Typography>
        <Typography variant="h6" 
          component="h2"
          sx={{
            color: 'text.light',
            fontSize: { xs: '0.9rem', md: '1.25rem' },
          }}
          >
          Recuerda que puedes cancelar tu plan en cualquier momento.
        </Typography>
        </Typography>

        

        <Grid container spacing={4} sx={{ px: { xs: 1, md: 0 } }}>
          {plans.map((plan, index) => (
            <Grid item key={index} xs={12} md={4}>
              <PlanCard {...plan} onSelectPlan={(price) => handleSelectPlan(price, plan)} />
            </Grid>
          ))}
        </Grid>

        <Dialog 
          open={showPayment} 
          onClose={() => setShowPayment(false)}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: {
              minHeight: { xs: '100vh', md: '80vh' },
              maxHeight: { xs: '100vh', md: '98vh' },
              width: { xs: '100%', md: '80%' },
              margin: { xs: 0, md: '20px' },
              position: 'relative'
            }
          }}
        >
          <IconButton
            onClick={() => setShowPayment(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 24,
              color: (theme) => theme.palette.grey[500],
              zIndex: 1
            }}
          >
            <CloseIcon />
          </IconButton>
          <PaymentForm price={selectedPrice} selectedPlan={selectedPlan} verify={selectedPlan == plans[0] ? false : true} />
        </Dialog>
      </Container>
    </GradientBackground>
  );
};

// Add the pulse animation keyframes at the bottom of the file
const pulseKeyframes = `
@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
  100% {
    opacity: 1;
  }
}
`;

// Add this style to the head of the document
const style = document.createElement('style');
style.innerHTML = pulseKeyframes;
document.head.appendChild(style);

export default PricesAndPlans; 